"use client"

import { useEffect, useRef, useState, useCallback } from "react"
import { useRouter, usePathname } from "next/navigation"
import { TrendingUp, TrendingDown, CheckCheck, Star, ChevronDown, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { useNotificationStore, PushNotification } from "@/store/notification-store"
import { useCommandStore } from "@/store/command-store"
import { useTokenIcons } from "@/hooks/use-token-icons"
import { useWatchlistStore } from "@/store/watchlist-store"
import { authFetch } from "@/lib/auth-fetch"
import { useVenueMarkets } from "@/hooks/use-venue-markets"
import { VenueBadges } from "@/components/ui/venue-badges"
import { AnalyticsWidgetShell } from "@/components/analytics-widgets/widget-shell"

export function timeAgo(ts: number): string {
  const diff = Date.now() - ts
  const m = Math.floor(diff / 60000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function extractEntry(body: string): string | null {
  const m = body.match(/Entry\s*\$?([\d,.]+)/i)
  return m ? `$${m[1]}` : null
}

export function extractSymbol(n: PushNotification): string {
  if (n.symbol) return n.symbol
  const m = n.title?.match(/([A-Z0-9]+USDT)\b/i)
  return m ? m[1].toUpperCase() : ""
}

const AVATAR_COLORS = [
  "bg-blue-500/15 text-blue-400",
  "bg-purple-500/15 text-purple-400",
  "bg-orange-500/15 text-orange-400",
  "bg-teal-500/15 text-teal-400",
  "bg-indigo-500/15 text-indigo-400",
  "bg-rose-500/15 text-rose-400",
  "bg-amber-500/15 text-amber-400",
  "bg-cyan-500/15 text-cyan-400",
]

function getAvatarColor(symbol: string): string {
  const hash = symbol.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0)
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

function AiVerdictBadge({ approved }: { approved: boolean | null | undefined }) {
  if (approved === true) return (
    <span className="text-[8px] font-bold px-1 py-0.5 rounded leading-none text-emerald-500 bg-emerald-500/10">
      ALGO OK
    </span>
  )
  if (approved === false) return (
    <span className="text-[8px] font-bold px-1 py-0.5 rounded leading-none text-red-500 bg-red-500/10">
      REJECT
    </span>
  )
  return null
}

export function SignalRow({
  notification,
  onClick,
}: {
  notification: PushNotification
  onClick: () => void
}) {
  const { getIcon } = useTokenIcons()
  const { toggleSymbol, isWatchlisted } = useWatchlistStore()
  const { getVenuesForCoin } = useVenueMarkets()
  const sym = extractSymbol(notification)
  const displaySymbol = sym ? sym.replace(/USDT$/i, "") : notification.title
  const iconUrl = getIcon(sym) || null
  const avatarColor = getAvatarColor(displaySymbol)
  const isLong = notification.direction?.toUpperCase() === "LONG"
  const hasDirection = !!notification.direction
  const entry = extractEntry(notification.body)
  const tag = notification.tag || notification.confidence || ""
  const starred = isWatchlisted(sym)

  const supportedVenues = getVenuesForCoin(displaySymbol)

  const [expanded, setExpanded] = useState(false)
  const [change24h, setChange24h] = useState<number | null>(null)
  useEffect(() => {
    if (!sym) return
    fetch(`https://fapi.binance.com/fapi/v1/ticker/24hr?symbol=${sym}`)
      .then(r => r.json())
      .then(d => { if (typeof d.priceChangePercent === 'string') setChange24h(parseFloat(d.priceChangePercent)) })
      .catch(() => {})
  }, [sym])

  return (
    <div className={cn("flex flex-col border-b border-border/50 last:border-0", !notification.read && "bg-primary/5")}>
      <div className="w-full flex items-center gap-2 px-2.5 py-2 transition-colors hover:bg-muted/50">
        {/* Unread dot */}
        <span className={cn("shrink-0 w-1.5 h-1.5 rounded-full", notification.read ? "bg-transparent" : "bg-primary")} />

        {/* Avatar + text — clicking here navigates */}
        <button
          onClick={onClick}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          {/* Avatar */}
          <div
            className={cn(
              "relative h-7 w-7 rounded-lg overflow-hidden shrink-0 flex items-center justify-center text-[10px] font-bold",
              avatarColor
            )}
          >
            <span className="select-none">{displaySymbol.slice(0, 2).toUpperCase()}</span>
            {iconUrl && (
              <img
                src={iconUrl}
                alt={displaySymbol}
                className="absolute inset-0 h-full w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none" }}
              />
            )}
          </div>

          {/* Text */}
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] font-semibold text-foreground truncate">{displaySymbol}</span>
              {hasDirection && (
                isLong
                  ? <TrendingUp className="h-3 w-3 text-emerald-500 shrink-0" />
                  : <TrendingDown className="h-3 w-3 text-red-500 shrink-0" />
              )}
            </div>
            <span className={cn(
              "text-[10px] font-mono tabular-nums",
              change24h === null ? "text-muted-foreground" :
              change24h >= 0 ? "text-emerald-500" : "text-red-500"
            )}>
              {change24h === null
                ? (entry ?? "Breakout signal")
                : `${change24h >= 0 ? "+" : ""}${change24h.toFixed(2)}%`
              }
            </span>
          </div>
        </button>

        {/* Star + badges + expand — clicking chevron expands, clicking star watchlists */}
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          <div className="flex items-center gap-1">
            <AiVerdictBadge approved={notification.ai_approved} />
            {tag && tag !== "NORMAL" && tag !== "LOW" && (
              <span className={cn(
                "text-[8px] font-bold px-1 py-0.5 rounded leading-none",
                tag === "HIGH"       ? "text-emerald-500 bg-emerald-500/10" :
                tag === "CVD"        ? "text-sky-500 bg-sky-500/10" :
                tag === "LIQ"        ? "text-red-500 bg-red-500/10" :
                tag === "WATCH"      ? "text-violet-500 bg-violet-500/10" :
                tag === "FEED"       ? "text-blue-500 bg-blue-500/10" :
                tag === "UNVERIFIED" ? "text-zinc-400 bg-zinc-400/10" :
                                       "text-amber-500 bg-amber-500/10"
              )}>
                {tag}
              </span>
            )}
            <button
              onClick={() => { if (sym) toggleSymbol(sym) }}
              className="text-muted-foreground hover:text-amber-400 transition-colors"
            >
              <Star className={cn("h-3 w-3", starred ? "fill-amber-400 text-amber-400" : "")} />
            </button>
            {notification.ai_reasoning && (
              <button
                onClick={() => setExpanded(e => !e)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
            )}
          </div>
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {timeAgo(notification.receivedAt)}
          </span>
          <VenueBadges venues={supportedVenues} size="xs" />
        </div>
      </div>

      {/* Expandable AI reasoning */}
      {expanded && notification.ai_reasoning && (
        <div className="px-3 pb-2 text-[10px] text-muted-foreground leading-relaxed border-t border-border/30 pt-1.5">
          {notification.ai_reasoning}
        </div>
      )}
    </div>
  )
}

// ─── Signal Log types ─────────────────────────────────────────────────────────

export interface SignalLogEntry {
  uuid: string
  symbol: string
  direction: string
  entry_price: number | null
  signal_type: string
  tag: string | null
  ai_approved: boolean | null
  ai_confidence: string | null
  ai_reasoning: string | null
  ai_key_factors: string[]
  created_at: string | null
}

export interface FeedSignalEntry {
  uuid: string
  symbol: string
  direction: string | null
  entry_min: number | null
  entry_max: number | null
  stop_loss: number | null
  tp1: number | null
  tp2: number | null
  tp3: number | null
  leverage: number | null
  confidence: number | null
  status: string | null
  verdict: string | null
  analysis: string | null
  entry_strategy: string | null
  urgency_level: string | null
  feed_name: string | null
  feed_provider: string | null
  received_at: string | null
}

export function SignalLogRow({ entry, onNavigate }: { entry: SignalLogEntry; onNavigate: (symbol: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const base = entry.symbol.replace(/USDT$/i, "")
  const isLong = entry.direction?.toUpperCase() === "LONG"
  const ts = entry.created_at ? new Date(entry.created_at).getTime() : Date.now()
  const { getVenuesForCoin } = useVenueMarkets()
  const signalVenues = getVenuesForCoin(base)

  return (
    <div className="flex flex-col border-b border-border/50 last:border-0">
      <button
        onClick={() => {
          if (expanded) {
            onNavigate(entry.symbol)
          } else {
            setExpanded(true)
          }
        }}
        className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-muted/50 transition-colors"
      >
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-semibold text-foreground">{base}</span>
            {isLong
              ? <TrendingUp className="h-3 w-3 text-emerald-500 shrink-0" />
              : <TrendingDown className="h-3 w-3 text-red-500 shrink-0" />
            }
            <span className="text-[9px] text-muted-foreground">{entry.signal_type}</span>
          </div>
          {entry.ai_reasoning && (
            <span className="text-[10px] text-muted-foreground truncate">{entry.ai_reasoning}</span>
          )}
        </div>

        <div className="flex flex-col items-end gap-0.5 shrink-0">
          <div className="flex items-center gap-1">
            {entry.ai_approved === true && (
              <span className={cn(
                "text-[8px] font-bold px-1 py-0.5 rounded leading-none",
                entry.ai_confidence === "HIGH"   ? "text-emerald-500 bg-emerald-500/10" :
                entry.ai_confidence === "NORMAL" ? "text-sky-500 bg-sky-500/10" :
                                                   "text-amber-500 bg-amber-500/10"
              )}>
                {entry.ai_confidence || "VERIFIED"}
              </span>
            )}
            {entry.ai_approved === false && (
              <span className="text-[8px] font-bold px-1 py-0.5 rounded leading-none text-zinc-400 bg-zinc-400/10">SKIPPED</span>
            )}
            {entry.ai_approved === null && (
              <span className="text-[8px] font-bold px-1 py-0.5 rounded leading-none text-zinc-400 bg-zinc-400/10">UNCONFIRMED</span>
            )}
            {(entry.ai_reasoning || (entry.ai_key_factors && entry.ai_key_factors.length > 0)) && (
              expanded ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
          </div>
          <span className="text-[10px] text-muted-foreground tabular-nums">{timeAgo(ts)}</span>
          <VenueBadges venues={signalVenues} size="xs" />
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-2 pt-1.5 border-t border-border/30 flex flex-col gap-1.5">
          {entry.ai_reasoning && (
            <p className="text-[10px] text-muted-foreground leading-relaxed">{entry.ai_reasoning}</p>
          )}
          {entry.ai_key_factors && entry.ai_key_factors.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {entry.ai_key_factors.map((f, i) => (
                <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{f}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}


export function FeedSignalRow({ entry, onNavigate }: { entry: FeedSignalEntry; onNavigate: (symbol: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const base = (entry.symbol || "").replace(/USDT$/i, "")
  const isLong = entry.direction?.toUpperCase() === "LONG"
  const ts = entry.received_at ? new Date(entry.received_at).getTime() : Date.now()
  const { getIcon } = useTokenIcons()
  const { getVenuesForCoin } = useVenueMarkets()
  const avatarColor = getAvatarColor(base)
  const iconUrl = getIcon(entry.symbol || "") || null
  const supportedVenues = getVenuesForCoin(base)

  const statusColor =
    entry.status === "EXECUTED" ? "text-emerald-500 bg-emerald-500/10" :
    entry.status === "REJECTED" ? "text-red-500 bg-red-500/10" :
    entry.status === "PENDING"  ? "text-amber-500 bg-amber-500/10" :
    "text-zinc-400 bg-zinc-400/10"

  const hasDetail = !!(entry.analysis || entry.verdict || entry.entry_strategy)

  return (
    <div className="flex flex-col border-b border-border/50 last:border-0">
      <button
        onClick={() => {
          if (expanded) {
            onNavigate(entry.symbol || "")
          } else {
            setExpanded(true)
          }
        }}
        className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-muted/50 transition-colors"
      >
        {/* Avatar */}
        <div
          className={cn(
            "relative h-7 w-7 rounded-lg overflow-hidden shrink-0 flex items-center justify-center text-[10px] font-bold",
            avatarColor
          )}
        >
          <span className="select-none">{base.slice(0, 2).toUpperCase()}</span>
          {iconUrl && (
            <img
              src={iconUrl}
              alt={base}
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none" }}
            />
          )}
        </div>

        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-semibold text-foreground">{base}</span>
            {entry.direction && (
              isLong
                ? <TrendingUp className="h-3 w-3 text-emerald-500 shrink-0" />
                : <TrendingDown className="h-3 w-3 text-red-500 shrink-0" />
            )}
            {entry.feed_name && (
              <span className="text-[9px] text-muted-foreground truncate max-w-[80px]">{entry.feed_name}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {entry.entry_min != null && (
              <span className="text-[10px] text-muted-foreground font-mono">Entry ${entry.entry_min.toFixed(4)}</span>
            )}
            {entry.tp1 != null && (
              <span className="text-[10px] text-emerald-600 font-mono">TP ${entry.tp1.toFixed(4)}</span>
            )}
            {entry.stop_loss != null && (
              <span className="text-[10px] text-red-600 font-mono">SL ${entry.stop_loss.toFixed(4)}</span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-0.5 shrink-0">
          <div className="flex items-center gap-1">
            {entry.status && (
              <span className={cn("text-[8px] font-bold px-1 py-0.5 rounded leading-none", statusColor)}>
                {entry.status}
              </span>
            )}
            {hasDetail && (
              expanded
                ? <ChevronDown className="h-3 w-3 text-muted-foreground" />
                : <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
          </div>
          <span className="text-[10px] text-muted-foreground tabular-nums">{timeAgo(ts)}</span>
          <VenueBadges venues={supportedVenues} size="xs" />
        </div>
      </button>

      {expanded && hasDetail && (
        <div className="px-3 pb-2 pt-1.5 border-t border-border/30 flex flex-col gap-1.5">
          {entry.verdict && (
            <p className="text-[10px] text-foreground font-medium">{entry.verdict}</p>
          )}
          {entry.analysis && (
            <p className="text-[10px] text-muted-foreground leading-relaxed">{entry.analysis}</p>
          )}
          {entry.entry_strategy && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground self-start">{entry.entry_strategy}</span>
          )}
        </div>
      )}
    </div>
  )
}

export function FeedSignalTab({ onNavigate, entries, loading }: { onNavigate: (symbol: string) => void; entries: FeedSignalEntry[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-[11px] text-muted-foreground">
        Loading...
      </div>
    )
  }
  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-[11px] text-muted-foreground">
        No feed signals yet
      </div>
    )
  }
  return (
    <div className="flex flex-col">
      {entries.map(e => <FeedSignalRow key={e.uuid} entry={e} onNavigate={onNavigate} />)}
    </div>
  )
}

export function SignalLogTab({ onNavigate, entries, loading }: { onNavigate: (symbol: string) => void; entries: SignalLogEntry[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-[11px] text-muted-foreground">
        Loading...
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-[11px] text-muted-foreground">
        No verified signals yet
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {entries.map(e => <SignalLogRow key={e.uuid} entry={e} onNavigate={onNavigate} />)}
    </div>
  )
}

// ─── Main widget ──────────────────────────────────────────────────────────────

interface SignalsWidgetProps {
  onClose: () => void
}

export function SignalsWidget({ onClose }: SignalsWidgetProps) {
  const { notifications, markRead, markAllRead } = useNotificationStore()
  const { setSelectedSymbol } = useCommandStore()
  const router = useRouter()
  const pathname = usePathname()
  const [activeTab, setActiveTab] = useState<"algo" | "feed" | "log">("algo")

  const algoNotifications = notifications.filter(n => n.tag !== "FEED")
  const algoUnread = algoNotifications.filter(n => !n.read).length

  const [feedEntries, setFeedEntries] = useState<FeedSignalEntry[]>([])
  const [feedLoading, setFeedLoading] = useState(true)

  const fetchFeedEntries = useCallback(() => {
    authFetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/exchange/feed-signal-log?limit=50`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setFeedEntries(data) })
      .catch(() => {})
      .finally(() => setFeedLoading(false))
  }, [])

  const [logEntries, setLogEntries] = useState<SignalLogEntry[]>([])
  const [logLoading, setLogLoading] = useState(true)

  const fetchLogEntries = useCallback(() => {
    authFetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/exchange/signal-log?limit=50`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setLogEntries(data) })
      .catch(() => {})
      .finally(() => setLogLoading(false))
  }, [])

  useEffect(() => {
    fetchFeedEntries()
    fetchLogEntries()
    const feedInterval = setInterval(fetchFeedEntries, 20_000)
    const logInterval = setInterval(fetchLogEntries, 20_000)
    return () => {
      clearInterval(feedInterval)
      clearInterval(logInterval)
    }
  }, [fetchFeedEntries, fetchLogEntries])

  const navigateToSymbol = useCallback(
    (sym: string) => {
      setSelectedSymbol(sym)
      const base = sym.replace(/USDT$/i, "").replace(/USDC$/i, "")
      if (pathname.startsWith("/terminal/")) {
        router.replace(`/terminal/futures/${base}`, { scroll: false } as any)
      } else {
        router.push(`/terminal/futures/${base}`)
      }
    },
    [setSelectedSymbol, router, pathname]
  )

  const handleSignalClick = useCallback(
    (n: PushNotification) => {
      markRead(n.id)
      const sym = extractSymbol(n)
      if (sym) navigateToSymbol(sym)
    },
    [markRead, navigateToSymbol]
  )

  const handleLogNavigate = useCallback(
    (symbol: string) => { navigateToSymbol(symbol) },
    [navigateToSymbol]
  )

  return (
    <AnalyticsWidgetShell id="signals" title="Signals" onClose={onClose} defaultWidth={280} defaultHeight={420}>
      <div className="flex flex-col text-xs overflow-hidden flex-1 min-h-0">
        {/* Tabs row */}
        <div className="flex items-center border-b border-border px-2 py-1.5 gap-2 shrink-0">
          <button
            onClick={() => setActiveTab("algo")}
            className={cn(
              "text-[11px] font-semibold transition-colors",
              activeTab === "algo" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Algo
          </button>
          {algoUnread > 0 && activeTab === "algo" && (
            <span className="text-[9px] font-bold bg-primary text-primary-foreground rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 leading-none">
              {algoUnread}
            </span>
          )}
          <span className="text-muted-foreground text-[10px]">/</span>
          <button
            onClick={() => setActiveTab("feed")}
            className={cn(
              "text-[11px] font-semibold transition-colors",
              activeTab === "feed" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Feed
          </button>
          <span className="text-muted-foreground text-[10px]">/</span>
          <button
            onClick={() => setActiveTab("log")}
            className={cn(
              "text-[11px] font-semibold transition-colors",
              activeTab === "log" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Algo Log
          </button>
          <div className="flex-1" />
          {activeTab === "algo" && algoUnread > 0 && (
            <button
              onClick={markAllRead}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors"
              title="Mark all read"
            >
              <CheckCheck className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
          {activeTab === "algo" ? (
            algoNotifications.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-[11px] text-muted-foreground">
                No signals yet
              </div>
            ) : (
              algoNotifications.map((n) => (
                <SignalRow key={n.id} notification={n} onClick={() => handleSignalClick(n)} />
              ))
            )
          ) : activeTab === "feed" ? (
            <FeedSignalTab onNavigate={handleLogNavigate} entries={feedEntries} loading={feedLoading} />
          ) : (
            <SignalLogTab onNavigate={handleLogNavigate} entries={logEntries} loading={logLoading} />
          )}
        </div>
      </div>
    </AnalyticsWidgetShell>
  )
}
